import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import TextField  from "@mui/material/TextField";

import { TABLE_NAME  as tableName, handlePaste, handleBeforeInput } from "../../../helpers";

const createNumberColumnFilter = () => {
  return ({ value, header, handleFilterSet }) => {
    const { t } = useTranslation();

    const OPERATORS = {
      '>=': '>=',
      '<=': '<=',
      '>': '>',
      '<': '<',
      '=': '=',
      '!=': '!=',
      '<>': '!='
    };
    
    const DEBOUNCE_DELAY = 500;
    const SEPARATOR = ";";
    
    const parseOperatorAndValue = useCallback((input) => {
      if (!input || typeof input !== 'string') return { operator: '', params: [] };
      
      const trimmedInput = input.trim();
      
      for (const [opSymbol, opKey] of Object.entries(OPERATORS)) {
        if (trimmedInput.startsWith(opSymbol)) {
          const valueStr = trimmedInput.substring(opSymbol.length).trim();
          if (valueStr) {
            const cleanValue = valueStr.replace(/,/g, "");
            const numValue = Number(cleanValue);
            if (!isNaN(numValue)) {
              return {
                operator: opKey,
                params: [numValue.toString()]
              };
            }
          }
          return { operator: opKey, params: [] };
        }
      }
      
      const values = input.split(SEPARATOR)
        .map(item => item.trim())
        .filter(item => item !== "")
        .map(item => {
          const clean = item.replace(/,/g, "");
          const num = Number(clean);
          return isNaN(num) ? item : num.toString();
        });
        
      return {
        operator: '',
        params: values
      };
    }, []);
    
    const splitAndTrim = useCallback((str) => {
      return str.split(SEPARATOR)
        .map(item => item.trim())
        .filter(item => item !== "");
    }, []);
    
    const formatNumber = useCallback((numStr) => {
      if (!numStr) return "";
      const clean = numStr.replace(/,/g, "");
      const num = Number(clean);
      return isNaN(num) ? "" : num.toLocaleString();
    }, []);
    
    const getDisplayValue = useCallback((val) => {
      if (!val) return "";
      
      if (typeof val === 'object' && val.operator !== undefined && val.params !== undefined) {
        if (val.params.length === 0) return val.operator || "";
        
        const formattedParams = val.params.map(param => formatNumber(param.toString()));
        
        return val.operator ? `${val.operator}${formattedParams.join(SEPARATOR)}` : formattedParams.join(SEPARATOR);
      }
    
      if (Array.isArray(val)) {
        if (val.length === 0) return "";
        
        return val
          .map(item => item ? formatNumber(item) : "")
          .join(SEPARATOR);
      }
      
      return "";
    }, [formatNumber]);
    
    const [updatedValue, setUpdatedValue] = useState(() => getDisplayValue(value));
    const [debounceTimer, setDebounceTimer] = useState(null);

    useEffect(() => {
      setUpdatedValue(getDisplayValue(value));
    }, [value, getDisplayValue]);
    
    useEffect(() => {
      if (Array.isArray(value) && value.length === 0) {
        setUpdatedValue("");
      } else if (typeof value === 'object' && value.params && value.params.length === 0) {
        setUpdatedValue("");
      }
    }, [value]);
    
    const handleChange = useCallback((e) => {
      const inputValue = e.target.value;
      setUpdatedValue(inputValue);
      
      if (!inputValue?.trim()) {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        handleFilterSet({ operator: '', params: [] });
      }
    }, [debounceTimer, handleFilterSet]);
    
    const handleKeyDown = useCallback((e) => {
      if (e.key === 'Enter') {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        
        const inputValue = e.target.value;
        
        if (!inputValue?.trim()) {
          handleFilterSet({ operator: '', params: [] });
          return;
        }
        
        const parsedValue = parseOperatorAndValue(inputValue);
        handleFilterSet(parsedValue);
      }
    }, [debounceTimer, handleFilterSet, parseOperatorAndValue]);
    
    const handleBlur = useCallback(() => {
      if (!updatedValue) return;
      
      const parsedValue = parseOperatorAndValue(updatedValue);
      if (parsedValue.operator && parsedValue.params.length > 0) {
        const formattedParams = parsedValue.params.map(param => formatNumber(param.toString()));
        setUpdatedValue(`${parsedValue.operator}${formattedParams.join(SEPARATOR)}`);
      } else if (!parsedValue.operator && parsedValue.params.length > 0) {
        const reformatted = parsedValue.params
          .map(param => formatNumber(param.toString()))
          .join(SEPARATOR);
        setUpdatedValue(reformatted);
      }
    }, [updatedValue, parseOperatorAndValue, formatNumber]);
    
    useEffect(() => {
      return () => {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
      };
    }, [debounceTimer]);
    
    return (
      <TextField
        size="small"
        fullWidth
        sx={{ mb: 1 }}
        label={`Filter ${tableName[header]}`}
        value={updatedValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onBeforeInput={handleBeforeInput}
        onPaste={handlePaste}
        inputProps={{
          inputMode: "numeric",
        }}
        title={t("number_title")}
        placeholder={t('placeholder_number_filter')}
      />
    );
  };
};

const createNumberColumnEditor = () => {
  return ({ value, onBlur, header }) => {
    const { t } = useTranslation();
    
    const formatNumber = useCallback((numStr) => {
      if (!numStr) return "";
      const clean = numStr.replace(/,/g, "");
      const num = Number(clean);
      return isNaN(num) ? null : num;
    }, []);
    
    const getDisplayValue = useCallback((val) => {
      if (val === null || val == '-' || val === undefined) return "";
      return formatNumber(val.toString());
    }, [formatNumber]);
    
    const [updatedValue, setUpdatedValue] = useState(() => getDisplayValue(value));
    
    useEffect(() => {
      setUpdatedValue(getDisplayValue(value));
    }, [value, getDisplayValue]);
    
    const handleChange = useCallback((e) => {
      const inputValue = e.target.value;
      setUpdatedValue(inputValue);
    }, []);
    
    const handleBlur = useCallback((e) => {
      const inputValue = e.target.value;
      
      if (!inputValue || inputValue.trim() === "") {
        onBlur(undefined);
        setUpdatedValue("");
        return;
      }
      
      const formattedValue = formatNumber(inputValue);
      if (formattedValue != null) {
        onBlur(formattedValue);
        setUpdatedValue(formattedValue);
      } else {
        onBlur(undefined);
        setUpdatedValue("");
      }
    }, [formatNumber, onBlur]);
    
    const handleFocus = useCallback((e) => {
      if (updatedValue) {
        const strValue = String(updatedValue);
        const unformattedValue = strValue.replace(/,/g, "");
        setUpdatedValue(unformattedValue);
      }
    }, [updatedValue]);
    return (
      <TextField
        type="text"
        size="small"
        fullWidth
        sx={{ mb: 1 }}
        value={updatedValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onBeforeInput={handleBeforeInput}
        onPaste={handlePaste}
        inputProps={{
          inputMode: "numeric"
        }}
        title={t("number_title_edit")}
        placeholder={t('placeholder_number_editable')}
      />
    );
  };
};

export { createNumberColumnFilter,createNumberColumnEditor };